import clsx from 'clsx'
import { forwardRef, type AllHTMLAttributes, type ElementType, type HTMLAttributes } from 'react'

type CardSize = 'zero' | 'small' | 'default' | 'big' | 'large'

type CardProps = AllHTMLAttributes<HTMLElement> & {
    as?: ElementType
    noBorder?: boolean
}

const Card = forwardRef<HTMLElement, CardProps>((props, ref): JSX.Element => {
    const { children, className, as: Component = 'div', noBorder = false, ...otherProps } = props

    return (
        <Component
            className={clsx(
                { 'border shadow-sm': !noBorder },
                'overflow-hidden rounded-xl border-neutral-200 transition dark:border-neutral-700 dark:bg-neutral-800 dark:shadow-neutral-800',
                className,
            )}
            ref={ref}
            {...otherProps}
        >
            {children}
        </Component>
    )
})

Card.displayName = 'Card'

type CardAlignType = 'left' | 'center' | 'right' | 'space'

type CardSubPartProps = HTMLAttributes<HTMLDivElement> & {
    as?: ElementType
    size?: CardSize
    align?: CardAlignType
    scrollHorizontally?: boolean
    noBorder?: boolean
}

const CardPart = forwardRef<HTMLDivElement, CardSubPartProps>((props, ref): JSX.Element => {
    const { as = 'div', align, className, scrollHorizontally, ...otherProps } = props
    const Component = as

    return (
        <Component
            className={clsx(
                'flex-wrap',
                { 'flex justify-start gap-4': align === 'left' },
                { 'flex justify-end gap-4': align === 'right' },
                { 'flex justify-center gap-4': align === 'center' },
                { 'flex justify-between gap-4': align === 'space' },
                scrollHorizontally &&
                    'overflow-x-auto [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-300 dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500 [&::-webkit-scrollbar-track]:bg-neutral-100 dark:[&::-webkit-scrollbar-track]:bg-neutral-700 [&::-webkit-scrollbar]:w-2',
                className,
            )}
            ref={ref}
            {...otherProps}
        />
    )
})

CardPart.displayName = 'CardPart'

const HeaderPartSize = {
    zero: '',
    small: 'px-4 py-3',
    default: 'px-4 py-3 md:px-6 md:py-4 ',
    big: 'px-6 py-4 md:px-8 md:py-6',
    large: 'px-8 py-6 md:px-10 md:py-8',
}
const ContentPartSize = {
    zero: '',
    small: 'px-4 py-3',
    default: 'px-4 py-3 md:px-6 md:py-4 ',
    big: 'px-6 py-4 md:px-8 md:py-6',
    large: 'px-8 py-6 md:px-10 md:py-8',
}
const FooterPartSize = {
    zero: '',
    small: 'px-4 py-3',
    default: 'px-4 py-3 md:px-6 md:py-4 ',
    big: 'px-6 py-4 md:px-8 md:py-6',
    large: 'px-8 py-6 md:px-10 md:py-8',
}

const CardHeader = forwardRef<HTMLDivElement, CardSubPartProps>((props, ref): JSX.Element => {
    const { className, size = 'default', noBorder, ...otherProps } = props
    return (
        <CardPart
            {...otherProps}
            as="header"
            className={clsx(
                { 'border-b': !noBorder },
                'border-neutral-200 dark:border-neutral-700',
                HeaderPartSize[size],
                className,
            )}
            ref={ref}
        />
    )
})

CardHeader.displayName = 'CardHeader'

const CardContent = forwardRef<HTMLDivElement, CardSubPartProps>((props, ref): JSX.Element => {
    const { className, size = 'default', ...otherProps } = props
    return <CardPart {...otherProps} as="div" className={clsx(className, ContentPartSize[size])} ref={ref} />
})

CardContent.displayName = 'CardContent'

const CardFooter = forwardRef<HTMLDivElement, CardSubPartProps>((props, ref): JSX.Element => {
    const { className, size = 'default', noBorder, ...otherProps } = props
    return (
        <CardPart
            {...otherProps}
            as="footer"
            className={clsx(
                { 'border-t': !noBorder },
                'border-neutral-200 dark:border-neutral-700',
                FooterPartSize[size],
                className,
            )}
            ref={ref}
        />
    )
})

CardFooter.displayName = 'CardFooter'

export { Card, CardContent, CardFooter, CardHeader }
